import { useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Skeleton } from '@/components/ui/Skeleton'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { useAlertDetail } from './hooks/useAlertDetail'
import { AlertHeader } from './components/detail/AlertHeader'
import { ResolutionAudit } from './components/detail/ResolutionAudit'
import { SlaBanner } from './components/detail/SlaBanner'
import { WhySection } from './components/detail/WhySection'
import { WhatSection } from './components/detail/WhatSection'
import { WhoTabs } from './components/detail/WhoTabs'
import { SuggestedGroupings } from './components/detail/SuggestedGroupings'
import { NotesTimeline } from './components/detail/NotesTimeline'
import { StatusHistoryTimeline } from './components/detail/StatusHistoryTimeline'
import { ActionRail } from './components/detail/ActionRail'
import { TriggeredActionPanel } from './components/detail/TriggeredActionPanel'
import { AlertScreeningPanel } from './components/detail/AlertScreeningPanel'

/**
 * AF-126 — alert detail page.
 *
 * Two-column layout: reading column on the left, sticky action rail on
 * the right. The reading column streams in priority order (header + SLA →
 * Why → What → Who → Trail) so analysts can start deciding before the page
 * is fully loaded.
 */
export function AlertDetailPage() {
  const { id } = useParams<{ id: string }>()
  // KUP-API-003: key the whole tree on the route id. React Router reuses
  // this element across /alerts/A -> /alerts/B, which used to carry the
  // previous alert's local state (open CloseDialog, note drafts, grouping
  // linker, pending mutations) over to the next alert. A key remounts the
  // subtree so every piece of local state starts clean for the new alert.
  return <AlertDetailBody key={id ?? ''} routeId={id} />
}

/** Case/brace/whitespace-insensitive UUID form for identity comparison. */
function canonicalUuid(v: string | undefined): string {
  return (v ?? '').trim().replace(/^\{|\}$/g, '').toLowerCase()
}

function AlertDetailBody({ routeId }: { routeId: string | undefined }) {
  const { data: alert, isLoading, isError, error, refetch } = useAlertDetail(routeId)
  const notesRef = useRef<HTMLDivElement | null>(null)

  if (isLoading) return <DetailSkeleton />
  if (isError) {
    // ErrorState shows server text for 4xx only (5xx may carry SQL text) and
    // gives 403/404 their own copy (e.g. alert belongs to another branch).
    return <ErrorState error={error} resource="this alert" onRetry={() => void refetch()} />
  }
  if (!alert) return null

  // Defensive integrity check: never render actions (close / escalate /
  // assign / notes) against a payload whose identity does not match the
  // alert the URL asked for — e.g. stale or placeholder data. Fail closed.
  if (canonicalUuid(alert.id) !== canonicalUuid(routeId)) {
    return (
      <Card role="alert">
        <CardHeader>
          <CardTitle>Couldn't verify alert</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-fg">
            The loaded alert does not match the requested one, so actions are disabled. Reload to
            try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Reload
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
      <div className="flex min-w-0 flex-col gap-4">
        <SlaBanner sla={alert.sla} status={alert.status} />
        <AlertHeader alert={alert} />
        {alert.resolution && <ResolutionAudit resolution={alert.resolution} />}
        <WhySection alert={alert} />
        {/* AF-SS-072: Sanctions/PEP screening explainability. Renders
            only when the alert has attached screening matches; on
            other alerts (rule-driven, ML, velocity) the panel
            silently returns null. */}
        <AlertScreeningPanel alertID={alert.id} />
        <TriggeredActionPanel alertId={alert.id} />
        <SuggestedGroupings alertId={alert.id} anchorAlert={alert} />
        <WhatSection transactionId={alert.transaction_id} />
        <WhoTabs relatedAlertIds={alert.related_alert_ids} />
        <NotesTimeline ref={notesRef} alertId={alert.id} notes={alert.notes} />
        <StatusHistoryTimeline history={alert.status_history} />
      </div>
      <div className="lg:sticky lg:top-4 lg:self-start">
        <ActionRail
          alert={alert}
          onJumpToAddNote={() => notesRef.current?.scrollIntoView({ behavior: 'smooth' })}
        />
      </div>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  )
}
